import { inject, Injectable, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';

/** How often to ask the service worker to compare the deployed ngsw.json
 *  against the running version. Long-lived SPA tabs are the norm here -
 *  power users keep CIQ open for days, which left them running weeks-old
 *  bundles (the "app launch opens a uuid-less URL" class of bug reports). */
const CHECK_INTERVAL_MS = 10 * 60 * 1000;

/** Interaction events that mark the page as non-pristine. Once any of
 *  these fires, we know the user has draft state and a silent reload
 *  could lose work. */
const INTERACTION_EVENTS: (keyof DocumentEventMap)[] = ['keydown', 'pointerdown', 'scroll'];

/** Which version hash the user has already dismissed the banner for.
 *  Keyed by hash, not a boolean: the point is that the next deploy gets a
 *  fresh chance to prompt, while the dismissed one stays dismissed. A
 *  predecessor toast was removed for re-firing every 10 minutes because it
 *  tracked nothing.
 *
 *  Read at VERSION_READY, so it suppresses any tab that has not yet been
 *  offered this version - a reload, or another tab whose SW reports the
 *  update later. It does NOT retract a banner already on screen elsewhere:
 *  there is no `storage` listener, so a second tab already prompting keeps
 *  prompting until it is reloaded (CIQ-1541). */
const DISMISSED_VERSION_KEY = 'ciq.swUpdate.dismissedVersion';

@Injectable({ providedIn: 'root' })
export class SwUpdateService {
    private readonly updates = inject(SwUpdate);

    private updateReady = false;

    /** True until the user interacts (types, clicks, scrolls). A pristine
     *  page has no draft state, so an immediate reload is safe. */
    private pristine = true;

    /** Hash of the staged version, set when VERSION_READY fires. Null until
     *  then, which is why `dismiss()` is a no-op before an update exists. */
    private pendingVersion: string | null = null;

    /** Drives the refresh banner. Only ever true on a non-pristine page:
     *  a pristine one self-reloads instead, so it never needs to ask. */
    readonly updateAvailable = signal(false);

    /** Call once at app startup. No-op when the service worker isn't
     *  active (dev mode, unsupported browser). */
    init(): void {
        if (!this.updates.isEnabled) {
            return;
        }

        this.updates.versionUpdates
            .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
            .subscribe(e => this.onUpdateReady(e.latestVersion.hash));

        // Broken SW cache (e.g. a lazy chunk that was deleted from S3 by a
        // later deploy's --delete before this client ever fetched it). The
        // only way out is a full reload, which re-fetches everything from
        // the network and re-seeds the SW with the current version.
        this.updates.unrecoverable.subscribe(() => this.reloadPage());

        // The SW only re-checks ngsw.json on full navigations, which an SPA
        // tab never performs - poll explicitly, and on tab re-focus (the
        // moment a user comes back to a days-old tab).
        setInterval(() => void this.safeCheck(), CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', () => this.onVisibilityChange(document.hidden));

        // Track user interaction to know when the page is no longer pristine.
        // Once any interaction fires, remove all listeners (one-shot).
        const markDirty = () => {
            this.pristine = false;
            for (const evt of INTERACTION_EVENTS) {
                document.removeEventListener(evt, markDirty, true);
            }
        };
        for (const evt of INTERACTION_EVENTS) {
            document.addEventListener(evt, markDirty, { capture: true, once: true });
        }

        void this.safeCheck();
    }

    /** Split out of `init()`'s listener purely so specs can drive it without
     *  dispatching a real `visibilitychange`. `init()` never removes that
     *  listener, so a dispatch reaches every service any earlier spec
     *  constructed - and those have had their `reloadPage` spy restored, so it
     *  really navigates and takes down the Karma run. */
    protected onVisibilityChange(hidden: boolean): void {
        if (hidden && this.updateReady && this.pristine) {
            // Tab went to the background with an update staged and nothing to
            // lose - reload now, while the user isn't looking, so they come
            // back to the current version without ever losing foreground state.
            //
            // `pristine` is load-bearing, not belt-and-braces. `updateReady` is
            // set before the pristine check in onUpdateReady and is never
            // cleared, so without it this fires on a tab that is SHOWING the
            // banner, or has DISMISSED it - hard-reloading away the composer
            // text that is the entire reason CIQ-1170 asks instead of
            // reloading. Reaching here pristine means CIQ-1147's immediate
            // reload did not happen, i.e. activateUpdate() rejected; this is
            // that tab's second chance.
            this.reloadPage();
            return;
        }
        if (!hidden) {
            void this.safeCheck();
        }
    }

    /** The single place this service navigates. Extracted as a test seam:
     *  `document.location` is non-configurable so it cannot be spied, and a
     *  real reload aborts the whole Karma run ("Some of your tests did a full
     *  page reload!"). Specs stub this instead. */
    protected reloadPage(): void {
        document.location.reload();
    }

    private async safeCheck(): Promise<void> {
        try {
            await this.updates.checkForUpdate();
        } catch {
            // SW in a broken state or offline - ignore, next tick retries.
        }
    }

    private onUpdateReady(hash: string): void {
        this.updateReady = true;
        this.pendingVersion = hash;

        // If the page is still pristine (no user interaction since load),
        // activate the new SW version and reload immediately. New tabs and
        // fresh page loads are pristine by definition, so they self-upgrade
        // within seconds of the SW detecting the new deploy (CIQ-1147).
        // activateUpdate() tells the SW to swap to the new version BEFORE
        // the reload, preventing a reload loop (without it, the reload
        // serves the old cached version and VERSION_READY fires again).
        if (this.pristine) {
            this.updates
                .activateUpdate()
                .then(() => this.reloadPage())
                .catch(() => {
                    // activation failed - leave updateReady set so the
                    // background-reload path still upgrades the tab later.
                });
            return;
        }

        // Non-pristine: the user has draft state, so reloading under them
        // would lose it. Ask instead -- unless they already declined THIS
        // version, in which case staying quiet until the next deploy is the
        // whole point of tracking the hash.
        if (this.readDismissedVersion() !== hash) {
            this.updateAvailable.set(true);
        }
    }

    /** Take the update now. Activates before reloading for the same reason
     *  the pristine path does: reloading first would re-serve the old cached
     *  version and fire VERSION_READY again. */
    applyUpdate(): void {
        this.updates
            .activateUpdate()
            .then(() => this.reloadPage())
            .catch(() => {
                // Activation failed, so a reload would just re-serve the old
                // version and re-prompt. Reload anyway: the SW's own
                // `unrecoverable` handler is the backstop for a truly wedged
                // cache, and leaving the banner up with a dead button is
                // worse than a reload that may no-op.
                this.reloadPage();
            });
    }

    /** Hide the banner until the next deploy. The tab keeps running the old
     *  version until the user reloads it themselves - the background-reload
     *  path deliberately does NOT override this (see `onVisibilityChange`),
     *  because a tab that declined is by definition one with draft state to
     *  lose. Declining costs that tab the update until it is next reloaded;
     *  that is the trade the banner exists to offer. */
    dismiss(): void {
        this.updateAvailable.set(false);
        if (this.pendingVersion === null) {
            return;
        }
        try {
            localStorage.setItem(DISMISSED_VERSION_KEY, this.pendingVersion);
        } catch {
            // Private mode or a full quota. The banner is already hidden for
            // this page; it may reappear on the next tab, which is a better
            // failure than throwing out of a click handler.
        }
    }

    private readDismissedVersion(): string | null {
        try {
            return localStorage.getItem(DISMISSED_VERSION_KEY);
        } catch {
            // Unreadable storage means we cannot prove they dismissed it.
            // Prompting again is the safe direction: the cost is a banner
            // they have seen before, versus never offering the update.
            return null;
        }
    }
}
